import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { getTags, createTag, archiveTag, moveTagToCategory, setTagAssignmentMode } from '@/services/api/tag.service'
import type { CreateTagRequest, TagAssignmentMode, TagTargetType } from '@/types/api/tag'

export function useTags(
  recordGroupId: string | undefined,
  targetType: TagTargetType
) {
  const queryClient = useQueryClient()
  const queryKey = ['tags', recordGroupId, targetType]

  const query = useQuery({
    queryKey,
    queryFn: () => getTags(recordGroupId!, targetType),
    enabled: !!recordGroupId,
    staleTime: 60 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: (body: Omit<CreateTagRequest, 'targetType'>) =>
      createTag(recordGroupId!, { ...body, targetType }),
    onSuccess: () => {
      // Invalidate after success — server is the source of truth (conflict errors come
      // from server, so optimistic append before success would need rollback on 409).
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (tagId: string) => archiveTag(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const moveMutation = useMutation({
    mutationFn: ({ tagId, categoryId }: { tagId: string; categoryId: string | null }) =>
      moveTagToCategory(tagId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const modeMutation = useMutation({
    mutationFn: ({ tagId, mode }: { tagId: string; mode: TagAssignmentMode }) => setTagAssignmentMode(tagId, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    tags: query.data ?? [],
    /** Tags the caller may apply (restricted ones need tag:use). */
    usableTags: (query.data ?? []).filter((tag) => tag.canUse !== false),
    setAssignmentMode: modeMutation.mutate,
    isSettingMode: modeMutation.isPending,
    moveTag: moveMutation.mutate,
    isMoving: moveMutation.isPending,
    isLoading: query.isLoading,
    error: query.error,
    createTag: createMutation.mutate,
    createTagAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    archiveTag: archiveMutation.mutate,
    isArchiving: archiveMutation.isPending,
  }
}
